import { useCallback, useEffect, useState } from "react";
import { Loader2, Eye, EyeOff, ShieldCheck, Lock, History } from "lucide-react";
import { api, type SelfProfileFact, type VaultAuditEntry } from "../lib/api";

/** /settings/profile/identity - Epic E3 Identity tab.
 *
 *  Shows the user's identity / documents / loyalty facts that Memso has
 *  accreted from boarding passes and e-tickets (KTN, frequent-flyer
 *  numbers, TSA PreCheck, seat/meal preferences). Critical fields (KTN)
 *  render masked by default; revealing the raw value requires a
 *  passphrase re-auth and is written to the vault audit log. */

const FIELD_LABELS: Record<string, string> = {
  ktn: "Known Traveler Number (KTN)",
  tsa_precheck: "TSA PreCheck",
  seat_preference: "Seat preference",
  meal_preference: "Meal preference",
  display_name: "Name",
};

function labelFor(fieldKey: string): string {
  if (FIELD_LABELS[fieldKey]) return FIELD_LABELS[fieldKey];
  if (fieldKey.startsWith("loyalty:")) {
    const rest = fieldKey.slice("loyalty:".length).replace(/_/g, " ");
    return `Frequent flyer (${rest})`;
  }
  return fieldKey.replace(/_/g, " ");
}

function reauthRequired(err: unknown): boolean {
  return err instanceof Error && /reauth_required/.test(err.message);
}

export function SelfIdentity() {
  const [facts, setFacts] = useState<SelfProfileFact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Per-field revealed plaintext (cleared on re-mask).
  const [revealed, setRevealed] = useState<Record<string, string>>({});
  const [revealBusy, setRevealBusy] = useState<string | null>(null);

  // Passphrase re-auth prompt state.
  const [prompting, setPrompting] = useState<string | null>(null);
  const [passphrase, setPassphrase] = useState("");
  const [reauthErr, setReauthErr] = useState<string | null>(null);

  // Audit log panel.
  const [showAudit, setShowAudit] = useState(false);
  const [audit, setAudit] = useState<VaultAuditEntry[]>([]);
  const [vaultAvailable, setVaultAvailable] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api
      .selfProfile()
      .then((r) => setFacts(r.facts ?? []))
      .catch((e) => setError(e instanceof Error ? e.message : "Failed to load"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => load(), [load]);

  const loadAudit = useCallback(() => {
    api
      .selfVaultAudit()
      .then((r) => {
        setAudit(r.entries ?? []);
        setVaultAvailable(r.vaultAvailable);
      })
      .catch(() => setAudit([]));
  }, []);

  // Attempt a reveal. On 403 (reauth_required), open the passphrase
  // prompt for that field.
  const doReveal = useCallback(
    async (field: string) => {
      setRevealBusy(field);
      try {
        const r = await api.selfReveal(field);
        setRevealed((m) => ({ ...m, [field]: r.value }));
        loadAudit();
      } catch (e) {
        if (reauthRequired(e)) {
          setPrompting(field);
          setPassphrase("");
          setReauthErr(null);
        } else {
          setError(e instanceof Error ? e.message : "Reveal failed");
        }
      } finally {
        setRevealBusy(null);
      }
    },
    [loadAudit],
  );

  const submitReauth = useCallback(async () => {
    if (!prompting) return;
    const field = prompting;
    setReauthErr(null);
    try {
      await api.selfReauth(passphrase);
      setPrompting(null);
      setPassphrase("");
      await doReveal(field);
    } catch (e) {
      setReauthErr(
        e instanceof Error && /reauth_unavailable/.test(e.message)
          ? "Vault passphrase is not configured on the server."
          : "Incorrect passphrase.",
      );
    }
  }, [prompting, passphrase, doReveal]);

  const reMask = (field: string) =>
    setRevealed((m) => {
      const next = { ...m };
      delete next[field];
      return next;
    });

  const identityFacts = facts.filter(
    (f) =>
      f.fieldCategory === "identity" ||
      f.fieldCategory === "documents" ||
      f.fieldCategory === "loyalty" ||
      f.fieldKey === "seat_preference" ||
      f.fieldKey === "meal_preference",
  );

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 space-y-5">
      <header className="space-y-1">
        <div className="text-xs uppercase tracking-wider font-bold text-slate-400 dark:text-slate-500">
          Self profile
        </div>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
          Identity
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Travel and identity details Memso has woven from your boarding
          passes and e-tickets. Sensitive numbers are masked; reveal needs a
          quick re-auth and is recorded in the vault audit log.
        </p>
      </header>

      {error && (
        <div className="rounded-lg border border-rose-300 bg-rose-50 dark:bg-rose-950/40 dark:border-rose-800 px-3 py-2 text-sm text-rose-700 dark:text-rose-300">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : identityFacts.length === 0 ? (
        <div className="glass rounded-xl border border-slate-200/70 dark:border-slate-700/50 p-5 text-sm text-slate-500 dark:text-slate-400">
          Nothing woven yet. As Memso processes your travel email, your KTN,
          frequent-flyer numbers, and preferences will appear here.
        </div>
      ) : (
        <div className="glass rounded-xl border border-slate-200/70 dark:border-slate-700/50 divide-y divide-slate-200/70 dark:divide-slate-700/50">
          {identityFacts.map((f) => {
            const isRevealed = revealed[f.fieldKey] != null;
            const critical = f.tier !== "standard";
            return (
              <div
                key={f.id}
                className="flex items-center justify-between gap-3 px-4 py-3"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-700 dark:text-slate-200">
                    {critical && <Lock className="w-3.5 h-3.5 text-amber-500" />}
                    {labelFor(f.fieldKey)}
                    {f.status === "inferred" && (
                      <span className="text-[10px] uppercase tracking-wide text-slate-400 border border-dashed border-slate-300 dark:border-slate-600 rounded px-1">
                        inferred
                      </span>
                    )}
                  </div>
                  <div className="font-mono text-sm text-slate-900 dark:text-slate-100 mt-0.5 truncate">
                    {isRevealed ? revealed[f.fieldKey] : f.value}
                  </div>
                </div>
                {critical && f.revealable && (
                  <button
                    onClick={() =>
                      isRevealed ? reMask(f.fieldKey) : doReveal(f.fieldKey)
                    }
                    disabled={revealBusy === f.fieldKey}
                    className="shrink-0 inline-flex items-center gap-1.5 rounded-md border border-slate-300 dark:border-slate-600 px-2.5 py-1.5 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-50"
                  >
                    {revealBusy === f.fieldKey ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : isRevealed ? (
                      <>
                        <EyeOff className="w-3.5 h-3.5" /> Hide
                      </>
                    ) : (
                      <>
                        <Eye className="w-3.5 h-3.5" /> Reveal
                      </>
                    )}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <button
        onClick={() => {
          const next = !showAudit;
          setShowAudit(next);
          if (next) loadAudit();
        }}
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-sky-600 dark:text-sky-400 hover:underline"
      >
        <History className="w-4 h-4" />
        {showAudit ? "Hide vault audit log" : "View vault audit log"}
      </button>

      {showAudit && (
        <div className="glass rounded-xl border border-slate-200/70 dark:border-slate-700/50 p-4 space-y-2">
          <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-700 dark:text-slate-200">
            <ShieldCheck className="w-4 h-4 text-emerald-500" /> Vault audit log
          </div>
          {!vaultAvailable && (
            <div className="text-xs text-amber-600 dark:text-amber-400">
              Vault encryption is not configured (MEMSO_VAULT_KEY unset). Reveal
              is disabled until an operator enables it.
            </div>
          )}
          {audit.length === 0 ? (
            <div className="text-sm text-slate-500">No vault activity yet.</div>
          ) : (
            <ul className="space-y-1 text-xs text-slate-600 dark:text-slate-400">
              {audit.map((e) => (
                <li key={e.id} className="flex items-center justify-between gap-2">
                  <span>
                    <span
                      className={
                        e.action === "reveal_granted"
                          ? "text-emerald-600 dark:text-emerald-400 font-semibold"
                          : e.action === "reveal_denied"
                            ? "text-rose-600 dark:text-rose-400 font-semibold"
                            : "text-slate-500 font-semibold"
                      }
                    >
                      {e.action.replace("_", " ")}
                    </span>
                    {e.fieldKey ? ` · ${labelFor(e.fieldKey)}` : ""}
                    {e.ip ? ` · ${e.ip}` : ""}
                  </span>
                  <span className="shrink-0 tabular-nums">
                    {new Date(e.createdAt).toLocaleString()}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {prompting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-sm rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-5 space-y-3 shadow-xl">
            <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-800 dark:text-slate-100">
              <Lock className="w-4 h-4 text-amber-500" /> Re-authenticate to reveal
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Enter your vault passphrase to reveal{" "}
              <span className="font-semibold">{labelFor(prompting)}</span>. This
              reveal is recorded in the audit log.
            </p>
            <input
              type="password"
              autoFocus
              value={passphrase}
              onChange={(e) => setPassphrase(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submitReauth()}
              placeholder="Vault passphrase"
              className="w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-950 px-2.5 py-1.5 text-sm"
            />
            {reauthErr && (
              <div className="text-xs text-rose-600 dark:text-rose-400">
                {reauthErr}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setPrompting(null);
                  setPassphrase("");
                  setReauthErr(null);
                }}
                className="rounded-md px-3 py-1.5 text-xs font-semibold text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                Cancel
              </button>
              <button
                onClick={submitReauth}
                disabled={!passphrase}
                className="rounded-md bg-sky-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-sky-700 disabled:opacity-50"
              >
                Unlock
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
